import SharedPreferences from "../../constants/SharedPreferences";
import {
  getErrorMessage,
  saveInStorage,
  getFromStorage,
  logApiResponse,
  saveEccCurvesFromResponse,
} from "../../util/Utility";
import {
  getAuthRestclient,
  getTwoFARestClient,
  getNonAuthRestClientForAdmin,
} from "../RestUtility";
import { aeadEncryptWithPasscode } from "../../crypto/AuthenticatedEncryption";
import BayunError from "../../constants/BayunError";
import { AuthenticateResponse } from "../../BayunCore";
import {
  getMessageFromSignatureString,
  retrieveAndverifyLastSignature,
} from "../../util/SignatureUtil";
import { derivePbkdf2Encoded } from "../../crypto/PBKDF2";
import { verifyAuthReponse } from "./internal/authVerification";
import { getTwoFAData } from "./twoFaApi";
import { getMemberLockBox } from "./memberApi";
import { LINKED_MEMBERS_KEY, setSessionItem } from "../../util/AppStorage";

export { loginViaValidatePasscode } from "./internal/authVerification";
export const changePasscodeImpl = async (
  sessionId,
  currentPasscode,
  newPasscode,
  successCallback,
  failureCallback,
) => {
  try {
    const orgMemberId = await getFromStorage(
      sessionId,
      SharedPreferences.ORG_MEMBER_ID,
    );
    if (currentPasscode == null || newPasscode == null) {
      throw BayunError.ERROR_NULL_PASSCODE;
    }
    const memberAppPrivateKey = await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_APP_PRIVATE_KEY,
    );
    const memberKeySalt = await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_KEY_SALT,
    );

    if (!memberKeySalt || !memberAppPrivateKey) {
      failureCallback(BayunError.ERROR_SOMETHING_WENT_WRONG);
      return;
    }

    const encryptedMemberAppPrivateKey = await aeadEncryptWithPasscode(
      memberAppPrivateKey,
      newPasscode,
      memberKeySalt,
      orgMemberId,
    );

    if (encryptedMemberAppPrivateKey != null) {
      const memberAuthSalt = await getFromStorage(
        sessionId,
        SharedPreferences.MEMBER_AUTH_SALT,
      );
      const currentAuthPwdHash = await derivePbkdf2Encoded(
        currentPasscode,
        memberAuthSalt,
      );
      const newAuthPwdHash = await derivePbkdf2Encoded(
        newPasscode,
        memberAuthSalt,
      );

      const authRestClient = await getAuthRestclient(sessionId);

      const request = {
        oldAuthPasscodeHash: currentAuthPwdHash,
        newAuthPasscodeHash: newAuthPwdHash,
        encryptedMemberAppPrivateKey: encryptedMemberAppPrivateKey,
      };

      const changePasscodeResponse = await authRestClient.post(
        "/changePasscode",
        request,
      );
      // console.log(changePasscodeResponse);
      await logApiResponse("changePasscodeResponse", changePasscodeResponse);
      successCallback({
        sessionId,
        securityQuestions: null,
        authenticationResponse: AuthenticateResponse.SUCCESS,
      });
    }
  } catch (error) {
    // removeLoadMask();
    console.log(error);
    console.log(error?.response?.data ?? error);
    failureCallback(getErrorMessage(sessionId, error?.response?.data ?? error));
  }
};

// export const getMemberPublicKeyImpl = async (
//   // sessionId,
//   appId,
//   appSecret,
//   orgName,
//   orgMemberId,
//   successCallback,
//   failureCallback,
// ) => {
//   try {
//     const restClient = getTwoFARestClient(
//       appId,
//       appSecret,
//       // lmsAppSecretKey
//     );

//     const getMemberPublicKeyResponse = await restClient.get(
//       "/getMemberPublicKey",
//       {
//         params: {
//           orgName: orgName,
//           orgMemberId: orgMemberId,
//         },
//       },
//     );
//     // console.log(getMemberPublicKeyResponse);
//     await logApiResponse(
//       "getMemberPublicKeyResponse",
//       getMemberPublicKeyResponse,
//     );

//     // const authRestClient = await getAuthRestclient(sessionId);
//     // const memberAppId = await getFromLocalStorage(
//     //   sessionId,
//     //   SharedPreferences.MEMBER_APP_ID
//     // );

//     // call the getMemberPublicKey API
//     // const getMemberPublicKeyResponse = await authRestClient.post(
//     //   "/memberPublicKey",
//     //   {
//     //     orgMemberId,
//     //     orgName,
//     //     memberAppId:null,
//     //     skipDetails: true
//     //   }
//     // );

//     // console.log(getMemberPublicKeyResponse);
//     successCallback({
//       // sessionId,
//       // securityQuestions: null,
//       authenticationResponse: AuthenticateResponse.SUCCESS,
//       memberPublicKey:
//         getMemberPublicKeyResponse.data.unAuthorizedMemberPublicKey,
//     });
//   } catch (error) {
//     console.log(error);
//     console.log(error.response.data);
//     failureCallback(getErrorMessage("", error.response.data));
//   }
// };


export async function loginWithAppCredentials(
  sessionId,
  orgName,
  orgMemberId,
  orgSwitchRequest,
  emailValidated,
  successCallback,
  failureCallback,
  securityQuestionsCallback,
  passphraseCallback,
  bayunCore,
) {
  let authResponse;

  if (!emailValidated) {
    await saveInStorage(sessionId, SharedPreferences.IS_PASSCODE_AUTH, "true");
  }

  // email not validated that means its a auth with passcode flow or a org switch request from admin panel ( this will trigger call to authenticateWithCredentials API)
  if (
    !emailValidated ||
    (orgSwitchRequest === true &&
      (await getFromStorage(sessionId, SharedPreferences.USER_ID)))
  ) {
    authResponse = await authenticateWithCredentials(
      sessionId,
      orgName,
      orgMemberId,
      orgSwitchRequest,
      emailValidated,
      failureCallback,
    );
  }

  // if it is a member swithching request from admin panel, no need to call getTwoFaData
  if (orgSwitchRequest) {
    console.log("Member Swithcing Request");
    await getMemberLockBox(sessionId, orgMemberId);
    successCallback({
      sessionId,
    });
    return;
  }

  // Admin panel: if a Bayun user is linked (or email already validated), always
  // continue with SQ/passphrase — ignore MFA setting.
  const userId =
    authResponse?.data?.userId ||
    (await getFromStorage(sessionId, SharedPreferences.USER_ID));
  const userExists = emailValidated === true || !!userId;

  if (userExists) {
    await getTwoFAData(
      sessionId,
      bayunCore,
      null,
      null,
      null,
      successCallback,
      failureCallback,
      securityQuestionsCallback,
      passphraseCallback,
      null, // authorizeMemberCallback
    );
  } else {
    // Unlinked member via app credentials: show apps and ask passcode
    successCallback({
      sessionId,
      loginMemberResponse: authResponse?.data,
    });
  }
}

export async function authenticateWithCredentials(
  sessionId,
  orgName,
  orgMemberId,
  orgSwitchRequest,
  emailValidated,
  failureCallback,
) {
  const restClient = getNonAuthRestClientForAdmin(sessionId);

  let response;
  try {
    response = await restClient.get("/admin/authenticateWithCredentials", {
      params: {
        orgName,
        orgMemberId,
        orgSwitchRequest,
        emailValidated,
      },
    });

    logApiResponse("authenticateWithCredentials", response);

    if (response.data.isAdmin === true || response.data.isAdmin === "true") {
      await saveInStorage(sessionId, SharedPreferences.IS_ADMIN, true);
    } else {
      await saveInStorage(sessionId, SharedPreferences.IS_ADMIN, false);
    }

    if (
      response.data.isSecurityAdmin === "true" ||
      response.data.isSecurityAdmin === true
    ) {
      await saveInStorage(sessionId, SharedPreferences.IS_SECURITY_ADMIN, true);
    } else {
      await saveInStorage(
        sessionId,
        SharedPreferences.IS_SECURITY_ADMIN,
        false,
      );
    }

    await saveInStorage(sessionId, SharedPreferences.ORG_NAME, orgName);

    await saveInStorage(
      sessionId,
      SharedPreferences.ORG_MEMBER_ID,
      orgMemberId,
    );

    await saveEccCurvesFromResponse(sessionId, response.data);

    await saveInStorage(
      sessionId,
      SharedPreferences.BAYUN_SERVER_PUBLIC_KEY,
      response.data.bayunServerPublicKey,
    );

    await saveInStorage(
      sessionId,
      SharedPreferences.ENCRYPTION_TYPE,
      response.data.encryptionType,
    );

    await saveInStorage(
      sessionId,
      SharedPreferences.MULTI_FACTOR_AUTH,
      response.data.multiFactorAuthentication,
    );

    // await saveInStorage(
    //   sessionId,
    //   SharedPreferences.USER_ID,
    //   getMessageFromSignatureString(authenticationWithEmailResp.data.userId)
    // );
    //reivew
    await saveInStorage(
      sessionId,
      SharedPreferences.USER_ID,
      response.data.userId,
    );

    // App-credentials login never sees the email the way the email login does, and
    // /admin/twofa/twoFAData omits it while the account is in recovery. This response is the only
    // point in the flow that still carries it, and the user-scoped signing contexts
    // ("userPublicKey$<email>") are unresolvable on the server without it. Only written when
    // present: the server leaves emailId null for users with no email or in PARTIALLY_LINKED
    // status, and overwriting with that would erase an email captured earlier in the session.
    if (response.data.emailId) {
      await saveInStorage(
        sessionId,
        SharedPreferences.EMAIL_ADDRESS,
        response.data.emailId,
      );
    }

    await saveInStorage(
      sessionId,
      SharedPreferences.MEMBER_AUTH_SALT,
      response.data.memberSalts.memberAuthSalt,
    );

    await saveInStorage(
      sessionId,
      SharedPreferences.MEMBER_KEY_SALT,
      response.data.memberSalts.memberKeySalt,
    );

    await verifyAuthReponse(sessionId, response, failureCallback);
  } catch (error) {
    if (error?.response?.data?.errorMessage) {
      failureCallback(error?.response?.data?.errorMessage ?? error);
    } else {
      failureCallback(BayunError.ERROR_SOMETHING_WENT_WRONG);
    }
    throw error;
  }
  return response;
}


export async function saveOrgAuthResponseForSwitching(
  sessionId,
  response,
  failureCallback,
) {
  const allLinkedMembers = response.data.orgAuthResponses;

  if (allLinkedMembers) {
    const updatedMembers = await Promise.all(
      allLinkedMembers.map(async (member) => {
        const verified = await retrieveAndverifyLastSignature(
          sessionId,
          member.memberId,
        );

        if (!verified) {
          console.log("memberId verification failed.");
          failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
          return { ...member, isDeveloper: false }; // mark explicitly
        }

        const newMemberId = getMessageFromSignatureString(member.memberId);

        return {
          ...member,
          memberId: newMemberId,
          isDeveloper: false, // normal members
        };
      }),
    );

    const email = await getFromStorage(
      sessionId,
      SharedPreferences.EMAIL_ADDRESS,
    );

    // If current user is developer → add a dev entry
    if (response.data.isDeveloper === "true") {
      const dev = {
        email, // from your saved email
        memberId: null,
        name: null,
        department: null,
        // ...add other fields you want as null
        isDeveloper: true,
      };
      if (
        (await getFromStorage(
          sessionId,
          SharedPreferences.LAST_LOGGED_IN_ACCOUNT_TYPE,
        )) === "DEVELOPER"
      ) {
        // if last logged in account type is developer,
        // then keep dev at 0th position in array to be defalut selected in UI
        updatedMembers.unshift(dev);
      } else {
        updatedMembers.push(dev);
      }
    }

    // keeping last logged in account type org name and org member id at the 0th position in the array
    const lastLoggedInAccountTypeOrgName = await getFromStorage(sessionId, SharedPreferences.ORG_NAME);
    const lastLoggedInAccountTypeOrgMemberId = await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID);

    // Find the index of the member to move to the 0th position
    const memberIndex = updatedMembers.findIndex(
      member =>
        member.orgName === lastLoggedInAccountTypeOrgName &&
        member.orgMemberId === lastLoggedInAccountTypeOrgMemberId
    );

    if (memberIndex > 0) {
      // Remove the member from its current position
      const [memberToMove] = updatedMembers.splice(memberIndex, 1);
      // Insert the member at the 0th position
      updatedMembers.unshift(memberToMove);
    }

    console.log("Saving linked users: ", updatedMembers);

    setSessionItem(sessionId, LINKED_MEMBERS_KEY, JSON.stringify(updatedMembers));
  }
}
